// src/components/TechnologiesSection/index.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const TechnologiesSection = () => {
  const { t } = useTranslation();
  
  // Teknoloji listesi - gerçek dosya yollarıyla
  const technologies = [
    {
      id: 'n8n',
      icon: '/N8n--Streamline-Simple-Icons.svg', // Bu dosyayı public klasörüne yerleştirin
      imageAlt: 'n8n logo'
    },
    {
      id: 'python',
      icon: '/python.ico', // Bu dosyayı public klasörüne yerleştirin
      imageAlt: 'Python logo'
    },
    {
      id: 'llama',
      icon: '/llama-icon.jpg', // Bu dosyayı public klasörüne yerleştirin
      imageAlt: 'Llama model logo'
    },
    {
      id: 'langchain',
      icon: '/Langchain-Icon.svg', // Bu dosyayı public klasörüne yerleştirin
      imageAlt: 'LangChain logo'
    },
    {
      id: 'selenium',
      icon: '/Selenium--Streamline-Simple-Icons.svg', // Bu dosyayı public klasörüne yerleştirin
      imageAlt: 'Selenium logo'
    },
    {
      id: 'tensorflow',
      icon: '/Tensorflow--Streamline-Simple-Icons.svg', // Bu dosyayı public klasörüne yerleştirin
      imageAlt: 'TensorFlow logo'
    },
    {
      id: 'zapier',
      icon: '/zapier-icon.svg', // Bu dosyayı public klasörüne yerleştirin
      imageAlt: 'Zapier logo'
    },
    {
      id: 'openai',
      icon: '/Openai--Streamline-Simple-Icons.svg', // Örnek OpenAI ikon
      imageAlt: 'OpenAI API logo'
    }
  ];

  // Animasyon varyantları
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <section className="py-16 bg-black">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-white">{t('technologies.title')} </span>
            <span className="text-cyan-400">{t('technologies.titleHighlight')}</span>
          </motion.h2>
          <motion.p 
            className="text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {t('technologies.subtitle')}
          </motion.p>
        </div>

        {/* Animasyonlu teknoloji kartları */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-6 relative"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {technologies.map((tech) => (
            <motion.div
              key={tech.id}
              className="bg-gray-900 border border-gray-800 rounded-xl p-6 flex flex-col items-center justify-center hover:border-cyan-500 transition-colors duration-300"
              variants={itemVariants}
              whileHover={{ 
                y: -10, 
                boxShadow: '0 10px 25px -5px rgba(0, 255, 255, 0.3)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="mb-4 text-cyan-400 transform transition-transform duration-500 hover:scale-110 hover:rotate-12">
                <div className="w-16 h-16 flex items-center justify-center">
                  <img 
                    src={tech.icon} 
                    alt={tech.imageAlt} 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
              <h3 className="text-lg font-medium text-white">{t(`technologies.techs.${tech.id}.name`)}</h3>
              <p className="text-sm text-gray-400 text-center mt-2">{t(`technologies.techs.${tech.id}.description`)}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Beceriler ve yetenekler hakkında alt bölüm */}
<motion.div 
  className="mt-16 bg-gray-900 rounded-xl p-8 border border-gray-800"
  initial={{ opacity: 0, y: 30 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6 }}
  viewport={{ once: true }}
>
  <div className="flex flex-col md:flex-row items-center">
    <div className="md:w-1/3 mb-6 md:mb-0 md:pr-8">
      <div className="relative">
        <div className="w-32 h-32 mx-auto bg-black rounded-full flex items-center justify-center border-4 border-cyan-500">
          <svg className="w-16 h-16 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        {/* Parlama efekti */}
        <div className="absolute -inset-1 rounded-full opacity-30 bg-cyan-500 blur-xl"></div>
      </div>
    </div>
    
    <div className="md:w-2/3">
      <h3 className="text-2xl font-bold mb-4 text-white">{t('technologies.expertise.title')}</h3>
      <p className="text-gray-300 mb-6">{t('technologies.expertise.description')}</p>
      
      <div className="space-y-6">
        {/* Uzmanlık alanları - yüzdelik olmadan */}
        {['automation', 'dataAnalysis', 'artificialIntelligence'].map((skill) => (
          <div key={skill} className="space-y-2">
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 flex items-center justify-center mr-3">
                {skill === 'automation' && (
                  <svg className="w-4 h-4 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V4L12 2 4 4v8c0 6 8 10 8 10z"></path>
                  </svg>
                )}
                {skill === 'dataAnalysis' && (
                  <svg className="w-4 h-4 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.21 15.89A10 10 0 118 2.83M22 12A10 10 0 0012 2v10z"></path>
                  </svg>
                )}
                {skill === 'artificialIntelligence' && (
                  <svg className="w-4 h-4 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 8v4l4 4"></path>
                  </svg>
                )}
              </div>
              <span className="text-lg font-medium text-white">{t(`technologies.expertise.skills.${skill}.name`)}</span>
            </div>
            <div className="pl-11">
              <p className="text-gray-400 text-sm">{t(`technologies.expertise.skills.${skill}.description`)}</p>
              <div className="flex mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg 
                    key={star} 
                    className="w-5 h-5 text-cyan-400 mr-1" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
</motion.div>
      </div>
    </section>
  );
};

export default TechnologiesSection;